import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../Utils/MapsLoader";

import './../../../style/Vars.css'
import './../../../style/ElementsStyle.css'
import './../../../style/ContainersStyles.css'
import '../Styles/Drivers.css'

function formatLatLng(lat, lng) {
  const f = (n) => n.toFixed(6);
  return `Ubicación seleccionada (${f(lat)}, ${f(lng)})`;
}

export default function MapPickerGoogle({ open, onClose, onConfirm, initialQuery = "" }) {
  const mapRef = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const geocoder = useRef(null);
  const autocomplete = useRef(null);

  const inputRef = useRef(null);

  const [query, setQuery] = useState(initialQuery || "");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [picked, setPicked] = useState(null); // { address, location }

  useEffect(() => {
    // Si el modal se está cerrando, salimos de inmediato
    if (!open) return;

    let mapClickListener = null;
    let dragendListener = null;
    let idleTimer;

    (async () => {
      setQuery(initialQuery || "");
      setPicked(null);
      setLoading(true);

      // Carga la API y las librerías
      try {
        await loadGoogleMaps();
      } catch (e) {
        console.error("Fallo crítico al cargar Google Maps:", e);
      }

      // Verificar que tanto Maps como Places estén disponibles
      if (!window.google?.maps || !window.google?.maps?.places) {
        setLoading(false);
        setReady(false);
        return;
      }
      setReady(true);

      // Crear mapa si no existe (la primera vez o después de la limpieza)
      if (!map.current) {
        map.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 19.4326, lng: -99.1332 }, // CDMX
          zoom: 13,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
        });
      }

      // Geocoder
      if (!geocoder.current) geocoder.current = new window.google.maps.Geocoder();

      // Marker
      if (!marker.current) {
        marker.current = new window.google.maps.Marker({
          map: map.current,
          draggable: true,
          visible: false, // oculto hasta seleccionar
          animation: window.google.maps.Animation.DROP,
        });
      } else {
        // Si el marcador ya existía (por un render previo) lo aseguramos al mapa
        marker.current.setMap(map.current);
        marker.current.setVisible(false);
      }

      // Drag del marker → reverse
      dragendListener = marker.current.addListener("dragend", () => {
        const ll = marker.current.getPosition();
        reverseGeocode(ll.lat(), ll.lng());
      });

      // Click en mapa → ubicar pin + reverse
      mapClickListener = map.current.addListener("click", (ev) => {
        const ll = ev.latLng;
        putMarker(ll.lat(), ll.lng(), true);
        reverseGeocode(ll.lat(), ll.lng());
      });

      // Autocomplete (Places)
      if (inputRef.current && !autocomplete.current && window.google.maps.places?.Autocomplete) {
        autocomplete.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ["place_id", "geometry", "formatted_address", "name"],
          types: ["geocode"],
          // componentRestrictions: { country: "mx" }, // opcional
        });
        autocomplete.current.addListener("place_changed", () => {
          const place = autocomplete.current.getPlace();
          const loc = place?.geometry?.location;
          if (!loc) return;
          const lat = loc.lat();
          const lng = loc.lng();
          const addr = place.formatted_address || place.name || formatLatLng(lat, lng);

          // Si trae viewport, ajusta a ese marco
          if (place.geometry.viewport) {
            map.current.fitBounds(place.geometry.viewport);
          } else {
            putMarker(lat, lng, true);
          }

          // Asegura marker visible y centrado
          putMarker(lat, lng, false);
          setPicked({ address: addr, location: { lat, lng } });
          setQuery(addr);
        });
      }

      // Si se abrió con texto: geocodifica y centra
      if ((initialQuery || "").trim()) {
        await geocodeText(initialQuery.trim(), true);
      }

      // CORRECCIÓN para el "mapa gris": Forzar el redimensionamiento y centrado al abrir el modal
      idleTimer = setTimeout(() => {
        if (map.current) {
          window.google.maps.event.trigger(map.current, "resize");
          // Forzar el centrado para que se muestre correctamente
          map.current.setCenter(map.current.getCenter());
        }
      }, 80);

      setLoading(false);
    })();

    // FUNCIÓN DE LIMPIEZA CLAVE
    return () => {
      // 1. Remover listeners
      if (mapClickListener) window.google.maps.event.removeListener(mapClickListener);
      if (dragendListener) window.google.maps.event.removeListener(dragendListener);
      clearTimeout(idleTimer);

      // 2. Desacoplar objetos de Google Maps y resetear referencias
      // Esto fuerza la creación de nuevas instancias al reabrir el modal
      if (marker.current) {
        marker.current.setMap(null); // Quita el marcador del mapa
      }

      // Reseteamos todas las referencias
      map.current = null;
      marker.current = null;
      geocoder.current = null;
      autocomplete.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function putMarker(lat, lng, center = true) {
    if (!map.current || !marker.current) return;
    const pos = { lat, lng };
    marker.current.setPosition(pos);
    marker.current.setVisible(true);
    if (center) {
      map.current.panTo(pos);
      const z = map.current.getZoom();
      if (!z || z < 15) map.current.setZoom(15);
    }
  }

  function reverseGeocode(lat, lng) {
    if (!geocoder.current) return;
    setLoading(true);
    geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
      setLoading(false);
      if (status === "OK" && results && results[0]) {
        const addr = results[0].formatted_address;
        setPicked({ address: addr, location: { lat, lng } });
        setQuery(addr);
      } else {
        const pretty = formatLatLng(lat, lng);
        setPicked({ address: pretty, location: { lat, lng } });
        setQuery(pretty);
        console.error("Geocoding Inverso falló:", status);
      }
    });
  }

  function geocodeText(text, jump = false) {
    return new Promise((resolve) => {
      if (!geocoder.current) return resolve(null);
      const q = (text || "").trim();
      if (!q) return resolve(null);
      setLoading(true);
      geocoder.current.geocode({ address: q }, (results, status) => {
        setLoading(false);
        if (status === "OK" && results && results[0]) {
          const r = results[0];
          const lat = r.geometry.location.lat();
          const lng = r.geometry.location.lng();
          const addr = r.formatted_address || q;

          if (r.geometry.viewport) {
            map.current.fitBounds(r.geometry.viewport);
          } else {
            putMarker(lat, lng, jump);
          }

          putMarker(lat, lng, false);
          setPicked({ address: addr, location: { lat, lng } });
          setQuery(addr);
          resolve({ address: addr, location: { lat, lng } });
        } else {
          console.error("Geocoding por texto falló:", status);
          resolve(null);
        }
      });
    });
  }

  if (!open) return null;

  return (
    <div className="map-overlay" role="dialog" aria-modal="true">
      <div className="map-panel">
        <div className="map-head">
          <input
            ref={inputRef}
            className="map-search"
            autoComplete="off"
            onFocus={(e) => {
              // Prevent default focus behavior that shows the keyboard
              e.preventDefault();
              // Only focus the input when the user explicitly taps on it
              const input = e.target;
              setTimeout(() => {
                input.focus({ preventScroll: true });
              }, 100);
            }}
            placeholder="Buscar dirección… (ej. Palacio de Bellas Artes)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                // Si el Autocomplete no está inicializado, usamos la geocodificación manual
                if (!autocomplete.current) {
                  await geocodeText(query, true);
                }
              }
            }}
          />
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" disabled={!picked} onClick={() => picked && onConfirm(picked)}>
            Confirmar
          </button>
        </div>

        <div className="map-body">
          {(!ready || loading) && (
            <div className="map-loading">
              {!ready
                ? "No se pudo cargar Google Maps. Revisa tu API key, billing, referrers y que Places/Geocoding API estén habilitadas."
                : "Buscando…"}
            </div>
          )}
          <div ref={mapRef} className="map-canvas" />
        </div>
      </div>
    </div>
  );
}